import '../services/i18nService.js';
import OptimizelyClientService from '../services/optimizelyClientService.js';
import { captureException } from '../services/sentryService.js';

const hasText = (input) => {
    return input && typeof input === 'string' && input.length;
};
/**
 * Convert a currency from Major Units to Minor Units.
 * <p>For example: From 529.95€ to 52995 cents.
 *
 * @param majorUnits The major units number
 * @return The number in minor units
 */
const convertMajorUnitsToMinorUnits = (majorUnits) => {
    return Math.round(majorUnits * 100);
};
const safeStringify = (value) => {
    // just in case the value is an object with circular references
    try {
        return JSON.stringify(value);
    }
    catch (error) {
        captureException({
            error,
        });
        return '';
    }
};
function objectHasKey(object, key) {
    return key in object;
}
function messageOfUnknownError(error) {
    const key = 'message';
    if (typeof error === 'undefined') {
        return 'undefined'; // we handle this explicitly because JSON.stringify does not output a string when passed undefined
    }
    else if (typeof error === 'string') {
        return error; // we already have a string
    }
    else if (typeof error === 'object' && error !== null && objectHasKey(error, key)) {
        const value = error[key]; // the message field is there, it should be a string but a test cannot hurt
        return typeof value === 'string' ? value : safeStringify(value);
    }
    else {
        return safeStringify(error);
    }
}
function defaultString(value, defaultValue = '') {
    return typeof value === 'string' ? value : defaultValue;
}
// require an explicit name (instead of using fn.name) in order to accept anonymous functions, for instance the ones created by useCallback
function exposeGlobally(name, fn) {
    window.Payment = {
        ...(window.Payment || {}),
        [name]: fn,
    };
    return () => {
        window.Payment[name] = undefined;
    };
}
function catchFatalError(onFatalError, fn) {
    return (...args) => {
        try {
            fn(...args);
        }
        catch (error) {
            onFatalError(error);
        }
    };
}
/**
 * <p>Convert the Zoo+ running environment to the appropriate Adyen environment. Zoo+ is running on the env such as
 * "dev" or "prod", while Adyen expects the environment to be "test" or "live".
 *
 * <p>At the moment, we have this env mapping:
 *   - "prod" (z+) = "live" (Adyen)
 *   - "dev"  (z+) = "test" (Adyen)
 *   - Others env from z+ = "test" (Adyen)
 *
 * @param runningEnv The Zoo+ running environment
 */
const convertRunningEnvToAdyenEnv = (runningEnv) => {
    return runningEnv === 'prod' ? 'live' : 'test';
};
const flipObject = (obj) => {
    return Object.fromEntries(Object.entries(obj).map((a) => a.reverse()));
};
const range = (n) => {
    return [...Array(n).keys()];
};
async function checkFeatureForUser(featureKey, attributes, optimizelyId) {
    const optimizely = new OptimizelyClientService();
    optimizely.initialize(optimizelyId);
    return await optimizely.isFeatureEnabled(featureKey, attributes);
}
/**
 * <p>Handles the "keydown" event on a specified element. If the key pressed is either the
 * "Enter" or "Space" key, the provided callback function will be invoked, and default event
 * behaviors (like form submission) will be prevented.</p>
 *
 * <i>Note: The function only triggers the callback for the "Enter" and "Space" keys. For any other key press,
 * no action will be taken.</i>
 *
 * @param event The keyboard event that was triggered by the key press.
 * @param callback A function to be called when the "Enter" or "Space" key is pressed. The callback will receive
 *                 the keyboard event as its argument.
 */
const handleKeyDown = (event, callback) => {
    if (callback && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        event.stopPropagation();
        callback(event);
    }
};

export { catchFatalError, checkFeatureForUser, convertMajorUnitsToMinorUnits, convertRunningEnvToAdyenEnv, defaultString, exposeGlobally, flipObject, handleKeyDown, hasText, messageOfUnknownError, objectHasKey, range, safeStringify };
//# sourceMappingURL=utils.js.map
